import {useNavigate, useParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {DateTime} from 'luxon';
import {ProgressBar} from 'react-bootstrap';
import {PageTitle} from '../../../_metronic/layout/core';
import Level3Title from '../../../app/common/components/title/Level3Title';
import EtatBadge from '../Components/EtatBadge';
import {getStatusColor, getStatusLabel} from '../Helpers/stateHelper';
import {useGetVpnDetail} from '../Hooks/ServiceManager';
import {useGetConsommation} from '../Hooks/ManageConsommation';
import {useGetGraph} from '../Hooks/MonitoringManager';
import {DEFAULT_GRAPH_PERIOD_HOURS} from '../Types/graphPeriods';
import {EtatLien, ListService} from '../Types/service';
import {ReclamationReason, ReclamationTypes} from '../../Ticket/Type/reclamation';
import NotFoundRedirect from '../../../app/common/components/NotFoundRedirect';
import {isNotFoundError} from '../../../app/common/tools/httpError';

const etatLienMap: Record<EtatLien, {color: string}> = {
  CONNECTED: {color: '#50cd89'},
  DISCONNECTED: {color: '#f1416c'},
  TO_CONFIGURE: {color: '#a1a5b7'},
};

interface InfoItemProps {
  label: string;
  value: string;
  factice?: boolean;
  fullWidth?: boolean;
}

const InfoItem = ({
  label,
  value,
  factice = false,
  fullWidth = false,
}: InfoItemProps): JSX.Element => (
  <div className={`${fullWidth ? 'col-12' : 'col-md-4'} mb-6`}>
    <div className="text-muted fs-7 mb-1">{label}</div>
    <div className={`fw-semibold fs-5 ${factice ? 'text-gray-500 fst-italic' : 'text-gray-800'}`}>
      {value}
    </div>
  </div>
);

interface StatBoxProps {
  label: string;
  value: string;
  factice?: boolean;
}

const StatBox = ({label, value, factice = false}: StatBoxProps): JSX.Element => (
  <div className="col-4">
    <div className="border border-gray-300 rounded p-4 h-100">
      <div className="text-muted fs-7 mb-1">{label}</div>
      <div className={`fw-bold fs-4 ${factice ? 'text-gray-500 fst-italic' : 'text-gray-800'}`}>
        {value}
      </div>
    </div>
  </div>
);

// Graphe de latence (ping) calqué sur GraphPopoverContent de /services/monitoring.
// Le backend renvoie une image PNG en base64 ; bu_id correspond ici à l'id du lien
// de monitoring (monitoring.id). Affiché uniquement si le graphe est disponible.
interface LatenceGraphProps {
  linkId: string;
}

const LatenceGraph = ({linkId}: LatenceGraphProps): JSX.Element | null => {
  const intl = useIntl();
  // Période fixe : dernières 24 heures.
  const {data, isLoading, isError} = useGetGraph(
    linkId,
    'ping-link',
    DEFAULT_GRAPH_PERIOD_HOURS,
    true
  );

  // Graphe indisponible (hors chargement/erreur) : ne rien afficher.
  if (!isLoading && !isError && !data?.base64) return null;

  return (
    <div>
      <div className="text-muted fs-7 fw-bold text-uppercase mb-3">{intl.formatMessage({id: 'LIEN.DETAIL.LATENCE.TITLE'})}</div>
      {isLoading && (
        <div className="text-center p-4">
          <span className="spinner-border spinner-border-sm text-primary" role="status" />
        </div>
      )}
      {isError && (
        <div className="text-center p-4 text-danger fs-7">{intl.formatMessage({id: 'LIEN.DETAIL.LATENCE.ERROR'})}</div>
      )}
      {!isLoading && !isError && data?.base64 && (
        <img
          src={`data:image/png;base64,${data.base64}`}
          alt={intl.formatMessage({id: 'LIEN.DETAIL.LATENCE.ALT'})}
          className="w-100"
        />
      )}
    </div>
  );
};

interface ConsommationLTECardProps {
  cisId: string;
}

const formatDataSize = (value: number, unit: string): {value: string; unit: string} => {
  const valueInBytes = unit.toLowerCase() === 'gb' ? value * 1024 : value;
  if (valueInBytes >= 1024) {
    return {value: (valueInBytes / 1024).toFixed(2), unit: 'GB'};
  }
  return {value: valueInBytes.toFixed(2), unit: 'MB'};
};

const ConsommationLTECard = ({cisId}: ConsommationLTECardProps): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading, isError} = useGetConsommation(cisId);

  if (isLoading) {
    return (
      <div className="card mt-5">
        <div className="card-body d-flex justify-content-center align-items-center py-10">
          <span className="spinner-border spinner-border-sm text-primary me-3" role="status" />
          <span className="text-muted">{intl.formatMessage({id: 'VPN.LOADING_CONSOMMATION'})}</span>
        </div>
      </div>
    );
  }

  if (isError || !data) return null;

  const {current = 0, limit = 0, unit = ''} = data;
  const percentage = limit > 0 ? (current / limit) * 100 : 0;

  let variant = 'success';
  let completionRate = limit > 0 ? Math.round((100 * current) / limit) : 0;
  if (completionRate >= 75 && completionRate < 90) {
    variant = 'info';
  }
  if (completionRate >= 90 && completionRate < 99) {
    variant = 'warning';
  }
  if (completionRate >= 99) {
    variant = 'danger';
  }
  const rate = Math.min(100, completionRate);

  const currentFormatted = formatDataSize(current, unit);
  const limitFormatted = formatDataSize(limit, unit);

  return (
    <div className="card mt-5">
      <div className="card-header border-0 pt-6">
        <div className="card-title">
          <Level3Title title={intl.formatMessage({id: 'VPN.LTE.TITLE'})} />
        </div>
      </div>
      <div className="card-body pt-2">
        <div className="row mb-5">
          <StatBox
            label={intl.formatMessage({id: 'VPN.LTE.CURRENT'})}
            value={`${currentFormatted.value} ${currentFormatted.unit}`}
          />
          <StatBox label={intl.formatMessage({id: 'VPN.LTE.LIMIT'})} value={`${limitFormatted.value} ${limitFormatted.unit}`} />
          <StatBox label={intl.formatMessage({id: 'VPN.LTE.PERCENTAGE'})} value={`${percentage.toFixed(2)} %`} />
        </div>
        <ProgressBar now={rate} variant={variant} />
        <div className="d-flex justify-content-between text-muted fs-7 mt-2">
          <span>
            {currentFormatted.value} {currentFormatted.unit}
          </span>
          <span>
            {limitFormatted.value} {limitFormatted.unit}
          </span>
        </div>
      </div>
    </div>
  );
};

const formatAdresse = (lien?: ListService): string => {
  const site = lien?.site;
  if (!site) return '—';
  return [site.address, [site.zipCode, site.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
};

const VpnDetailPage = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {serviceId} = useParams<{serviceId: string}>();

  const {data: lien, isLoading, isError, error} = useGetVpnDetail(serviceId ?? '');

  const RetourButton = (): JSX.Element => (
    <button
      type="button"
      className="btn btn-sm btn-outline btn-outline-primary btn-active-light-primary d-inline-flex align-items-center mb-5"
      onClick={() => navigate('/services/connexion/vpn')}
    >
      <i className="bi bi-arrow-left me-2" />
      {intl.formatMessage({id: 'COMMON.BACK'})}
    </button>
  );

  if (isLoading) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.CONNECTIONS.VPN'})}</PageTitle>
        <RetourButton />
        <div className="card">
          <div className="card-body d-flex justify-content-center align-items-center py-20">
            <span className="spinner-border text-primary me-3" role="status" />
            <span className="text-muted">{intl.formatMessage({id: 'VPN.LOADING'})}</span>
          </div>
        </div>
      </>
    );
  }

  if (isNotFoundError(error)) {
    return <NotFoundRedirect />;
  }

  if (isError || !lien) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.CONNECTIONS.VPN'})}</PageTitle>
        <RetourButton />
        <div className="card">
          <div className="card-body py-10 text-center text-muted">
            <i className="bi bi-exclamation-triangle fs-2x text-warning d-block mb-3" />
            {intl.formatMessage({id: 'VPN.ERROR'})}{serviceId}
          </div>
        </div>
      </>
    );
  }

  const monitoring = lien.monitoring;
  const miseEnProduction = lien.deliveryDate
    ? DateTime.fromISO(lien.deliveryDate).setLocale('fr').toFormat('d LLLL yyyy')
    : null;
  const etat = (monitoring?.etat as EtatLien | undefined) ?? 'TO_CONFIGURE';
  const etatInfo = etatLienMap[etat];
  const etatLabel = intl.formatMessage({id: `LIEN.DETAIL.STATE.${etat}`});
  const enLigne = etat === 'CONNECTED';

  const ouvrirTicket = () => {
    const params = new URLSearchParams();
    if (lien.site?.id) params.append('site', lien.site.id.toString());
    if (lien.serviceId) params.append('service', lien.serviceId.toString());
    const search = params.toString() ? `?${params.toString()}` : '';
    navigate(`/support${search}`, {
      state: {
        reclamationData: {
          type: ReclamationTypes.TECHNICAL,
          motif: ReclamationReason.DEGRADATION_OF_SERVICE,
          objet: `${lien.serviceName || lien.link_name} (${lien.serviceId})`,
        },
      },
    });
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.CONNECTIONS.VPN'})}</PageTitle>

      <RetourButton />

      {/* En-tête du lien */}
      <div className="card mb-5">
        <div className="card-body d-flex flex-wrap align-items-start justify-content-between gap-4">
          <div className="d-flex align-items-start">
            <div className="symbol symbol-50px me-4">
              <span className="symbol-label bg-light-primary">
                <i className="bi bi-diagram-3 fs-2 text-primary" />
              </span>
            </div>
            <div>
              <h2 className="fs-2 fw-bold text-gray-900 mb-1">
                {lien.serviceName || lien.link_name}
              </h2>
              <div className="text-muted fs-6 mb-3">
                {lien.serviceId} · {lien.buName} · {formatAdresse(lien)}
              </div>
              <div className="d-flex flex-wrap gap-2">
                <EtatBadge
                  color={getStatusColor(lien.state)}
                  label={getStatusLabel(lien.state, intl) || '-'}
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-primary d-inline-flex align-items-center"
            onClick={ouvrirTicket}
          >
            <i className="bi bi-chat-left-text me-2" />
            {intl.formatMessage({id: 'SUPPORT.NEW_REQUEST'})}
          </button>
        </div>
      </div>

      <div className="row g-5">
        {/* Informations du lien */}
        <div className="col-lg-7">
          <div className="card h-100">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <Level3Title title={intl.formatMessage({id: 'VPN.INFO.TITLE'})} />
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="row">
                <InfoItem label={intl.formatMessage({id: 'VPN.INFO.ID'})} value={String(lien.serviceId)} />
                <InfoItem label={intl.formatMessage({id: 'VPN.INFO.TYPE'})} value={lien.type?.join(', ') || '—'} />
                <InfoItem label={intl.formatMessage({id: 'VPN.INFO.SPEED'})} value={monitoring?.debit || 'N/A'} />
                <InfoItem label={intl.formatMessage({id: 'VPN.INFO.IP'})} value={monitoring?.ip || 'N/A'} />
                <InfoItem label={intl.formatMessage({id: 'VPN.INFO.MES'})} value={miseEnProduction || 'N/A'} />
                <InfoItem
                  label={intl.formatMessage({id: 'VPN.INFO.SITE'})}
                  fullWidth
                  value={
                    [lien.site?.company, formatAdresse(lien)]
                      .filter((part) => part && part !== '—')
                      .join(' - ') || '—'
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* État opérationnel */}
        <div className="col-lg-5">
          <div className="card h-100">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <Level3Title title={intl.formatMessage({id: 'VPN.INFO.STATE'})} />
              </div>
            </div>
            <div className="card-body pt-2">
              <div
                className={`d-flex align-items-center rounded p-4 mb-5 ${
                  enLigne ? 'bg-light-success' : 'bg-light-danger'
                }`}
              >
                <span
                  className="rounded-circle me-3"
                  style={{width: 10, height: 10, backgroundColor: etatInfo.color, flexShrink: 0}}
                />
                <div className="d-flex flex-column">
                  <span className="fw-bold fs-5 text-gray-800">{etatLabel}</span>
                </div>
              </div>

              <div className="row g-3 mb-5">
                <StatBox label={intl.formatMessage({id: 'VPN.INFO.LATENCY'})} value={monitoring?.latence_ping || '-'} />
              </div>

              {monitoring?.ping_graph && monitoring?.id && <LatenceGraph linkId={monitoring.id} />}
            </div>
          </div>
        </div>
      </div>

      {/* Consommation LTE : provider provient du endpoint détail (/api/services/{id}) */}
      {lien.is4g && <ConsommationLTECard cisId={String(lien.serviceId)} />}
    </>
  );
};

export default VpnDetailPage;
